import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import DatasetOptionContent from './DatasetOptionContent';
import { outlinedPickerSx } from '~/theme/outlinedPickerSx';
import { PropagationDoseChoice } from './usePropagationDoseChoice';

const NONE = '';

interface DosePropagationSelectProps {
    choice: PropagationDoseChoice;
}

export default function DosePropagationSelect({
    choice,
}: DosePropagationSelectProps) {
    const intl = useIntl();
    const { options, selectedSopInstanceUid, select } = choice;

    if (options.length === 0) {
        return null;
    }

    const value = selectedSopInstanceUid ?? NONE;
    const labelOf = (sopInstanceUid: string) =>
        options.find((option) => option.sopInstanceUid === sopInstanceUid)
            ?.label ?? intl.formatMessage({ id: 'adapt.dose.unnamed' });

    return (
        <FormControl
            fullWidth
            sx={outlinedPickerSx(value ? 'primary' : 'neutral')}
        >
            <InputLabel shrink id="propagation-dose-select-label" size="small">
                <FormattedMessage id="dose" />
            </InputLabel>
            <Select
                notched
                data-testid="adaptDoseSelect"
                labelId="propagation-dose-select-label"
                id="propagation-dose-select"
                label={intl.formatMessage({ id: 'dose' })}
                value={value}
                displayEmpty
                size="small"
                onChange={(event) =>
                    select(
                        event.target.value === NONE ? null : event.target.value
                    )
                }
                renderValue={(selected) =>
                    selected ? (
                        <SensitiveInfoTypography>
                            <Typography component="span" fontSize={14}>
                                {labelOf(selected)}
                            </Typography>
                        </SensitiveInfoTypography>
                    ) : (
                        <Typography fontSize={14} color="text.secondary">
                            <FormattedMessage id="adapt.dose.none" />
                        </Typography>
                    )
                }
                MenuProps={{ PaperProps: { sx: { mt: 0.5 } } }}
            >
                {options.map((option) => (
                    <MenuItem
                        key={option.sopInstanceUid}
                        value={option.sopInstanceUid}
                    >
                        <DatasetOptionContent
                            label={option.label}
                            fallbackLabel={intl.formatMessage({
                                id: 'adapt.dose.unnamed',
                            })}
                            modality="RTDOSE"
                            uid={option.sopInstanceUid}
                        />
                    </MenuItem>
                ))}
                <MenuItem value={NONE}>
                    <Typography fontSize={14} color="text.secondary">
                        <FormattedMessage id="adapt.dose.none" />
                    </Typography>
                </MenuItem>
            </Select>
        </FormControl>
    );
}
